import { MapPin, Clock, Phone, Mail, ChevronDown, Facebook } from 'lucide-react'

const STORE_HOURS: Record<string, string> = {
  Monday: '11 AM-8:30 PM',
  Tuesday: '11 AM-8:30 PM',
  Wednesday: '11 AM-8:30 PM',
  Thursday: '11 AM-8:30 PM',
  Friday: '11 AM-8:30 PM',
  Saturday: '10 AM-8:30 PM',
  Sunday: '10 AM-8:30 PM',
}

const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

function getCentralDayName() {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    timeZone: 'America/Chicago',
  }).format(new Date())
}

const storeInfo = {
  address: '816 S Rand Rd, Lake Zurich, IL 60047',
  mapUrl: 'https://www.google.com/maps/dir/?api=1&destination=816+S+Rand+Rd,+Lake+Zurich,+IL+60047',
  phone: '(847) 454-3362',
  phoneHref: 'tel:+18474543362',
  email: 'contact@shreesaistore.com',
  emailHref: 'mailto:contact@shreesaistore.com',
  facebookUrl: 'https://www.facebook.com/shreesaigrocers',
}

export default function StoreDetails() {
  const centralToday = getCentralDayName()
  const todayHours = STORE_HOURS[centralToday] ?? 'Check with store for current hours'
  const otherDays = DAY_ORDER.filter((day) => day !== centralToday)

  return (
    <section
      className="overflow-hidden rounded-2xl border border-primary-100 bg-white shadow-lg"
      aria-label="Store contact"
    >
      {/* Phones: three compact lines (address · hours + phone · email + Facebook). */}
      <div className="space-y-2 px-3 py-2.5 text-xs text-gray-700 sm:hidden">
        <a
          href={storeInfo.mapUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex min-w-0 items-center gap-1.5 hover:text-primary-700"
        >
          <MapPin className="h-3.5 w-3.5 shrink-0 text-primary-600" aria-hidden />
          <span className="truncate">{storeInfo.address}</span>
        </a>
        <div className="flex items-start justify-between gap-3">
          <details className="group min-w-0">
            <summary className="flex cursor-pointer list-none items-center gap-1.5 [&::-webkit-details-marker]:hidden">
              <Clock className="h-3.5 w-3.5 shrink-0 text-primary-600" aria-hidden />
              <span>
                <span className="font-semibold">Today</span> {todayHours}
              </span>
              <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform group-open:rotate-180" aria-label="All days" />
            </summary>
            <ul className="mt-1 space-y-0.5 pl-5 text-gray-600">
              {otherDays.map((day) => (
                <li key={day}>
                  <span className="font-medium">{day}</span> {STORE_HOURS[day]}
                </li>
              ))}
            </ul>
          </details>
          <a href={storeInfo.phoneHref} className="flex shrink-0 items-center gap-1.5 text-primary-700">
            <Phone className="h-3.5 w-3.5 text-primary-600" aria-hidden />
            {storeInfo.phone}
          </a>
        </div>
        <div className="flex items-center justify-between gap-3">
          <a href={storeInfo.emailHref} className="flex min-w-0 items-center gap-1.5 text-primary-700">
            <Mail className="h-3.5 w-3.5 shrink-0 text-primary-600" aria-hidden />
            <span className="truncate">{storeInfo.email}</span>
          </a>
          <a
            href={storeInfo.facebookUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex shrink-0 items-center gap-1.5 text-primary-700"
            aria-label="Facebook – Shree Sai Grocers"
          >
            <Facebook className="h-3.5 w-3.5 text-primary-600" aria-hidden />
            Facebook
          </a>
        </div>
      </div>

      <div className="hidden p-4 sm:block lg:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center shrink-0">
              <MapPin className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <a
                href={storeInfo.mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
              >
                <h3 className="font-semibold text-gray-800 group-hover:text-primary-700">
                  Visit us
                </h3>
                <p className="text-sm text-gray-600 group-hover:underline">{storeInfo.address}</p>
              </a>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center shrink-0">
              <Clock className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800">Store hours</h3>
              <p className="text-sm text-gray-700">
                <span className="font-semibold">{centralToday}</span> {todayHours}
              </p>
              <details className="mt-1">
                <summary className="text-sm text-primary-600 cursor-pointer hover:underline">View all days</summary>
                <ul className="mt-2 space-y-1 text-sm text-gray-600">
                  {otherDays.map((day) => (
                    <li key={day}>
                      <span className="font-medium">{day}</span> {STORE_HOURS[day]}
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center shrink-0">
              <Phone className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800">Phone</h3>
              <a href={storeInfo.phoneHref} className="text-sm text-primary-600 hover:underline">
                {storeInfo.phone}
              </a>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center shrink-0">
              <Mail className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800">Get in touch</h3>
              <a
                href={storeInfo.emailHref}
                className="text-sm text-primary-600 hover:underline block"
              >
                {storeInfo.email}
              </a>
              <a
                href={storeInfo.facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary-600 hover:underline block mt-1"
              >
                Facebook – Shree Sai Grocers
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
